import { getServerDate } from "../utils/storage/date";
import * as logger from "../utils/logger";
import { Trailer, ITrailer } from "./Trailer";
import { IHashMap, IModifiedType } from "../sync/sync.types";
import constants from "../constants";
import { makeTrailerCache } from "../utils/storage/cache";
import { noEntityIds } from "../sync/common";

const NO_TRAILER_ID = "NoTrailerId";
import { DriveStore } from "../store/reducers";

export interface ITrailerAttachment extends IHashMap<ITrailerAttachment> {
    id: string;
    device: IIdEntity;
    trailer: IIdEntity;
    activeFrom: string;
    activeTo?: string;
    version?: string;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    _isTemp?: boolean;
}

export class TrailerAttachment implements ITrailerAttachment {
    id: string;
    device: IIdEntity;
    trailer: IIdEntity;
    activeFrom: string;
    activeTo?: string;

    modified?: IModifiedType;
    previousState?: ITrailerAttachment;
    /* eslint-disable @typescript-eslint/naming-convention */
    _syncCounter?: number;

    _isTemp?: boolean;

    constructor (obj?: ITrailerAttachment) {
        if (obj && Object.keys(obj).length > 0) {
            this.id = obj.id || "";
            this.device = obj.device || { id: "NoDeviceId" };
            this.trailer = obj.trailer || { id: NO_TRAILER_ID };
            this.activeFrom = obj.activeFrom;
            this.activeTo = obj.activeTo || constants.MAX_DATE.toISOString();

            this.modified = obj.modified;
            this.previousState = obj.previousState;
            this._syncCounter = obj._syncCounter || 5;

            this._isTemp = obj._isTemp || false;
        } else {
            this.id = "";
            this.device = { id: "NoDeviceId" };
            this.trailer = { id: NO_TRAILER_ID };
            this.activeFrom = getServerDate().toISOString();
            this.activeTo = constants.MAX_DATE.toISOString();

            this._syncCounter = 5;

            this._isTemp = false;
        }
    }
    /* eslint-enable @typescript-eslint/naming-convention */

    detach () {
        let oneSecondAgo = getServerDate();
        oneSecondAgo.setTime(oneSecondAgo.getTime() - 1);
        this.activeTo = oneSecondAgo.toISOString();
        // Report error if activeTo is lower than activeFrom
        let activeFrom = this.activeFrom;
        if (activeFrom && activeFrom > oneSecondAgo.toISOString()) {
            logger.error(logger.Source.ERROR, `Current date: ${ new Date().toString() }, Detach: ${ JSON.stringify(this) }`);
            throw new Error("TrailerAttachment activeFrom is higher than activeTo");
        }
        return this;
    }

    getAttachmentTrailer(store: DriveStore) {
        // Without this an absent trailer produced a Trailer with an undefined id: an object that
        // looks real to every caller but matches nothing, which is harder to trace than the sentinel.
        if (this.hasNoTrailerId()) {
            return new Trailer({ id: NO_TRAILER_ID } as ITrailer);
        }
        let allTrailers: IIdDictionary<ITrailer> = makeTrailerCache(store).getDict();
        let trailerId = this.trailer.id;
        return new Trailer(allTrailers[trailerId] || { id: trailerId });
    }

    toJSON(): ITrailerAttachment {
        return {
            id: this.id,
            device: this.device,
            trailer: this.trailer,
            activeFrom: this.activeFrom,
            activeTo: this.activeTo,
            modified: this.modified,
            previousState: this.previousState
        };
    }

    hasNoTrailerId(): boolean {
        return !this.trailer || !this.trailer.id || noEntityIds.indexOf(this.trailer.id) !== -1;
    }

    hasNoDeviceId(): boolean {
        return !this.device || !this.device.id || noEntityIds.indexOf(this.device.id) !== -1;
    }
}